/**
 * ViewNav — Sidebar section [2]: `nav` slot.
 *
 * Three-button switcher between stage views:
 *   - Authors           → home route (AuthorListView)
 *   - Influence network → influence-graph route
 *   - Theme map         → theme-map route
 *
 * Listens to `stage:route-changed` (emitted by StageRouter with the full
 * Route object as payload) to keep the active button in sync when navigation
 * is triggered elsewhere (clicking an author card etc.).
 */

import { BaseSection, type SectionId } from '@/core/BaseSection';
import type { Locale } from '@/bridge/bridge-types';
import type { Route } from '@/views/StageRouter';
import { escapeHtml } from '@/core/escape';
import { icon } from '@/core/icons';

type NavKey = 'home' | 'influence-graph' | 'theme-map';

const NAV_ITEMS: Array<{ key: NavKey; event: string; i18n: string; icon: string }> = [
  { key: 'home',             event: 'navigate:home',             i18n: 'nav.authors',        icon: 'authors' },
  { key: 'influence-graph',  event: 'navigate:influence-graph',  i18n: 'nav.influenceGraph', icon: 'influence' },
  { key: 'theme-map',        event: 'navigate:theme-map',        i18n: 'nav.themeMap',       icon: 'themeMap' },
];

export class ViewNav extends BaseSection {
  readonly sectionId: SectionId = 'nav';
  readonly sectionTitle = 'View navigation';

  private buttons: Map<NavKey, HTMLButtonElement> = new Map();

  protected async onInit(): Promise<void> {
    this.render();
    this.addCleanup(
      this.context.bus.on<Route>('stage:route-changed', (route) => {
        // The 'author' detail route is treated as a sub-state of 'home'
        // (clicking an author card from the list).
        const activeKey: NavKey =
          route.kind === 'home' || route.kind === 'author' ? 'home' :
          route.kind === 'influence-graph' ? 'influence-graph' :
          'theme-map';
        this.setActive(activeKey);
      }),
    );
  }

  protected onLocaleChange(_locale: Locale): void {
    this.render();
  }

  private listenerAttached = false;

  private render(): void {
    this.buttons.clear();
    this.element.classList.add('view-nav');
    this.element.innerHTML = `
      <div class="view-nav-inner">
        ${NAV_ITEMS.map(item => `
          <button type="button"
                  class="view-nav-btn${item.key === 'home' ? ' is-active' : ''}"
                  data-route="${item.key}">
            <span class="view-nav-icon" aria-hidden="true">${icon(item.icon)}</span>
            <span>${escapeHtml(this.context.i18n.t(item.i18n))}</span>
          </button>
        `).join('')}
      </div>
    `;

    for (const item of NAV_ITEMS) {
      const btn = this.element.querySelector<HTMLButtonElement>(`[data-route="${item.key}"]`)!;
      this.buttons.set(item.key, btn);
    }

    // Single delegated click listener; attached once for the lifetime of the
    // component so re-renders (locale change) don't pile up listeners.
    if (!this.listenerAttached) {
      this.addEventListener(this.element, 'click', (ev) => {
        const btn = (ev.target as HTMLElement).closest<HTMLElement>('[data-route]');
        if (!btn) return;
        const item = NAV_ITEMS.find((n) => n.key === btn.dataset.route);
        if (!item) return;
        this.setActive(item.key);
        this.context.bus.emit(item.event, {});
      });
      this.listenerAttached = true;
    }
  }

  private setActive(key: NavKey): void {
    for (const [k, btn] of this.buttons.entries()) {
      btn.classList.toggle('is-active', k === key);
    }
  }
}

